import { startOfWeek, endOfWeek, subWeeks, format, isWithinInterval } from 'date-fns';

/**
 * Utility functions for Weekly Summary
 * Pure functions for date calculations and data aggregation
 */

/**
 * Get start and end dates for a week
 * @param date - Date within the week
 * @returns Object with startDate and endDate
 */
export function getWeekDateRange(date: Date): { startDate: Date; endDate: Date } {
  const startDate = startOfWeek(date, { weekStartsOn: 0 }); // Sunday
  const endDate = endOfWeek(date, { weekStartsOn: 0 }); // Saturday
  return { startDate, endDate };
}

/**
 * Get previous week's date range
 * @param date - Current date
 * @returns Object with startDate and endDate for previous week
 */
export function getPreviousWeekRange(date: Date): { startDate: Date; endDate: Date } {
  const previousWeek = subWeeks(date, 1);
  return getWeekDateRange(previousWeek);
}

/**
 * Get date range for last N weeks
 * @param date - Current date
 * @param weeks - Number of weeks to go back
 * @returns Array of week ranges
 */
export function getLastNWeeks(date: Date, weeks: number): Array<{ startDate: Date; endDate: Date }> {
  const ranges: Array<{ startDate: Date; endDate: Date }> = [];
  for (let i = 0; i < weeks; i++) {
    const weekDate = subWeeks(date, i);
    ranges.push(getWeekDateRange(weekDate));
  }
  return ranges;
}

/**
 * Format date range for display
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Formatted string (e.g., "Jan 1 - Jan 7, 2024")
 */
export function formatDateRange(startDate: Date, endDate: Date): string {
  if (startDate.getFullYear() === endDate.getFullYear()) {
    if (startDate.getMonth() === endDate.getMonth()) {
      return `${format(startDate, 'MMM d')} - ${format(endDate, 'd, yyyy')}`;
    }
    return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
  }
  return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
}

/**
 * Check if a date is within a date range
 * @param date - Date to check
 * @param startDate - Range start
 * @param endDate - Range end
 * @returns True if date is within range
 */
export function isDateInRange(date: Date, startDate: Date, endDate: Date): boolean {
  return isWithinInterval(date, { start: startDate, end: endDate });
}

/**
 * Calculate percentage change between two numbers
 * @param current - Current value
 * @param previous - Previous value
 * @returns Percentage change (positive or negative)
 */
export function calculatePercentageChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

/**
 * Generate text summary for export
 * @param summaryData - Summary data object
 * @returns Formatted text string
 */
export function generateTextSummary(summaryData: {
  weekRange: string;
  rocksCreated: number;
  rocksCompleted: number;
  rocksDropped: number;
  issuesCreated: number;
  issuesResolved: number;
  actionItemsCreated: number;
  actionItemsCompleted: number;
  metricsCreated: number;
  winsExcitedCount: number;
}): string {
  return `
EOS Weekly Summary
${summaryData.weekRange}

ROCKS:
- Created: ${summaryData.rocksCreated}
- Completed: ${summaryData.rocksCompleted}
- Dropped: ${summaryData.rocksDropped}

ISSUES:
- Created: ${summaryData.issuesCreated}
- Resolved: ${summaryData.issuesResolved}

ACTION ITEMS:
- Created: ${summaryData.actionItemsCreated}
- Completed: ${summaryData.actionItemsCompleted}

METRICS:
- Created: ${summaryData.metricsCreated}

WINS & EXCITED:
- Entries: ${summaryData.winsExcitedCount}
`.trim();
}

